/*!
 * UAE BTS – Cart pair guard
 * Keeps the promotional subscription + free bottle as a single unit in the cart.
 * Only affects line items that carry the `_uae_bts_campaign` property
 * (set in sections/uae-bts-partners.liquid). All other cart items behave normally.
 */
(function () {
  'use strict';

  if (window.__UAE_BTS_CART_PAIR_GUARD__) return;
  window.__UAE_BTS_CART_PAIR_GUARD__ = true;

  var CAMPAIGN = '_uae_bts_campaign';
  var BOTTLE = '_uae_bts_bottle';
  var isOnCartPage = window.location.pathname.indexOf('/cart') !== -1;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function url(path) {
    return root + path.replace(/^\/+/, '');
  }

  function getCart() {
    return fetch(url('cart.js'), {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function changeLine(key, quantity) {
    return fetch(url('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error(data.description || data.message || 'Unable to update cart.');
        return data;
      });
    });
  }

  // ---- Which items belong to this promotion? ----
  // Markers written by uae-bts-partners.liquid:
  //   subscription: { _uae_bts_campaign: 'true', _uae_bts_bottle_variant_id: '...' } + a selling plan
  //   free bottle : { _uae_bts_campaign: 'true', _uae_bts_bottle: 'true' }
  function isCampaign(item) {
    return item && item.properties && item.properties[CAMPAIGN] === 'true';
  }
  function isSubscription(item) {
    return isCampaign(item) && !!item.selling_plan_allocation;
  }
  function isBottle(item) {
    return isCampaign(item) && item.properties && item.properties[BOTTLE] === 'true';
  }

  // Cached info so we can act without extra round-trips.
  var campaignKeys = new Set();
  var campaignVariantIds = new Set();
  var campaignIndexes = new Set();
  var variantIdToKey = {};
  var indexToKey = {};
  var pair = { subKey: null, botKey: null, subQty: 0, botQty: 0 };

  function refreshFromCart(cart) {
    campaignKeys = new Set();
    campaignVariantIds = new Set();
    campaignIndexes = new Set();
    variantIdToKey = {};
    indexToKey = {};
    pair = { subKey: null, botKey: null, subQty: 0, botQty: 0 };
    (cart.items || []).forEach(function (item) {
      variantIdToKey[item.id] = item.key;
      indexToKey[item.index] = item.key;
      if (!isCampaign(item)) return;
      campaignKeys.add(item.key);
      campaignVariantIds.add(item.id);
      campaignIndexes.add(item.index);
      if (isSubscription(item)) {
        pair.subKey = item.key;
        pair.subQty = item.quantity;
      } else if (isBottle(item)) {
        pair.botKey = item.key;
        pair.botQty = item.quantity;
      }
    });
  }

  function refreshFromServer() {
    return getCart().then(refreshFromCart).catch(function () {});
  }
  // ---- Loader ----
  var loader = null;

  function ensureStyles() {
    if (document.getElementById('uae-bts-pair-loader-style')) return;
    var style = document.createElement('style');
    style.id = 'uae-bts-pair-loader-style';
    style.textContent =
      '.uae-bts-pair-loader{position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;' +
      'background:rgba(255,255,255,.55);display:flex;align-items:center;justify-content:center;' +
      'flex-direction:column;gap:12px;font-family:inherit;font-size:15px;color:#333}' +
      '.uae-bts-pair-loader__spinner{width:34px;height:34px;border:3px solid rgba(0,0,0,.15);' +
      'border-top-color:#000;border-radius:50%;animation:uaeBtsSpin .7s linear infinite}' +
      '@keyframes uaeBtsSpin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }

  function showLoader() {
    if (loader) return;
    ensureStyles();
    loader = document.createElement('div');
    loader.className = 'uae-bts-pair-loader';
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');
    loader.innerHTML = '<span class="uae-bts-pair-loader__spinner"></span><span>Updating your offer…</span>';
    document.body.appendChild(loader);
  }

  function hideLoader() {
    if (loader) {
      if (loader.parentNode) loader.parentNode.removeChild(loader);
      loader = null;
    }
  }

  // ---- Apply a target quantity to BOTH paired lines at once ----
  function applyPairQuantity(qty) {
    qty = Math.max(0, parseInt(qty, 10) || 0);

    function run() {
      var subKey = pair.subKey;
      var botKey = pair.botKey;
      var changes = [];
      if (subKey && pair.subQty !== qty) changes.push(changeLine(subKey, qty));
      if (botKey && pair.botQty !== qty) changes.push(changeLine(botKey, qty));
      if (!changes.length) return Promise.resolve(null);

      showLoader();
      return Promise.all(changes)
        .then(function () { return getCart(); })
        .then(function (newCart) {
          hideLoader();
          refreshFromCart(newCart);
          try {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: newCart } }));
          } catch (e) {}
          // Reflect the change on the cart page (single, quick reload).
          if (isOnCartPage) window.location.reload();
          return newCart;
        })
        .catch(function (err) {
          hideLoader();
          console.warn('[UAE BTS] Pair change failed:', err);
          return null;
        });
    }

    // Fast path: use the cached pair.
    if (pair.subKey || pair.botKey) return run();

    // Cold start: fetch the cart first, then run.
    return getCart().then(function (cart) {
      refreshFromCart(cart);
      return run();
    }).catch(function (err) {
      console.warn('[UAE BTS] Could not read cart:', err);
      return null;
    });
  }

  // ---- Identify a row as part of the promotion ----
  function keyForRow(row, element) {
    var key = null;
    var node = row || element;
    if (node) {
      key = node.getAttribute('data-key') || node.getAttribute('data-line-key');
      if (!key) {
        var vid = node.getAttribute('data-variant-id');
        if (vid && variantIdToKey[vid]) key = variantIdToKey[vid];
      }
      if (!key) {
        var idx = node.getAttribute('data-index');
        if (idx && indexToKey[idx]) key = indexToKey[idx];
      }
    }
    if (!key && element) {
      var m = /updates\[([^\]]+)\]/.exec(element.name || '');
      if (m) key = m[1];
      if (!key && element.hasAttribute('data-index')) {
        var idx2 = element.getAttribute('data-index');
        if (idx2 && indexToKey[idx2]) key = indexToKey[idx2];
      }
    }
    return key;
  }

  function isCampaignRow(row, element) {
    var key = keyForRow(row, element);
    return !!key && campaignKeys.has(key);
  }

  function rowSelector() {
    return '.cart-item, .cart__item, [data-cart-item]';
  }

  // ---- Intercept clicks on campaign controls ONLY ----
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    // + / - quantity buttons
    var btn = target.closest(
      '.quantity__button[name="minus"], .quantity__button[name="plus"], ' +
      '.quantity__button[data-quantity-minus], .quantity__button[data-quantity-plus]'
    );
    if (btn) {
      var row = btn.closest(rowSelector());
      var input = row && row.querySelector('.quantity__input');
      if (!row || !input) return;
      if (!isCampaignRow(row, input)) return;
      var current = parseInt(input.value, 10);
      current = isNaN(current) ? 0 : current;
      var isPlus = btn.getAttribute('name') === 'plus' || btn.hasAttribute('data-quantity-plus');
      var targetQty = isPlus ? current + 1 : Math.max(0, current - 1);
      event.preventDefault();
      event.stopImmediatePropagation();
      applyPairQuantity(targetQty);
      return;
    }

    // Remove controls
    var control = target.closest(
      'cart-remove-button, a[href*="/cart/change"], button[name="remove"], ' +
      '[data-cart-remove], [data-cart-item-remove]'
    );
    if (control) {
      var row2 = control.closest(rowSelector());
      if (!isCampaignRow(row2, control)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      applyPairQuantity(0);
    }
  }, true);

  // ---- Intercept typed quantity changes for campaign rows ----
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.classList || !target.classList.contains('quantity__input')) return;
    if (!isCampaignRow(target.closest(rowSelector()), target)) return;
    event.stopImmediatePropagation();
    var qty = parseInt(target.value, 10);
    if (isNaN(qty)) return;
    applyPairQuantity(qty);
  }, true);

  // ---- Keep the cached pair / line keys up to date ----
  document.addEventListener('cart:updated', function (event) {
    var cart = event && event.detail && event.detail.cart;
    if (cart) refreshFromCart(cart);
    else setTimeout(refreshFromServer, 50);
  });

  document.addEventListener('cart:refresh', function () {
    setTimeout(refreshFromServer, 50);
  });

  // ---- Lightweight orphan / quantity safety net (cart page only) ----
  if (isOnCartPage) {
    var orphanLock = false;
    function checkOrphans() {
      if (orphanLock) return;
      orphanLock = true;
      getCart().then(function (cart) {
        var items = cart.items || [];
        var subs = items.filter(isSubscription);
        var bots = items.filter(isBottle);
        var changes = [];
        subs.slice(1).forEach(function (l) { changes.push(changeLine(l.key, 0)); });
        bots.slice(1).forEach(function (l) { changes.push(changeLine(l.key, 0)); });
        var sub = subs[0];
        var bot = bots[0];
        if (sub && bot && sub.quantity !== bot.quantity) {
          changes.push(changeLine(bot.key, sub.quantity));
        } else if (sub && !bot) {
          changes.push(changeLine(sub.key, 0));
        } else if (!sub && bot) {
          changes.push(changeLine(bot.key, 0));
        }
        if (!changes.length) return null;
        return Promise.all(changes).then(function () { return getCart(); });
      }).then(function (c) {
        if (c) {
          try {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: c } }));
          } catch (e) {}
          window.location.reload();
        }
      }).catch(function () {}).finally(function () { orphanLock = false; });
    }
    setInterval(checkOrphans, 4000);
  }

  // ---- Init ----
  refreshFromServer();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshFromServer);
  }
})();

